import { CompanyType } from '@treadinc/horizon-api-spec';

import { CompanyBasic } from '~hooks/useCompany/models';
import { Driver, DriverBasic, DriverTypeahead } from '~hooks/useDrivers/models';

export function getConsolidatedEquipmentName(
  driver: Driver | DriverBasic | DriverTypeahead,
) {
  return (
    driver.companyShare?.defaultEquipment?.externalId ||
    driver.companyShare?.defaultEquipment?.name ||
    driver.defaultEquipment?.externalId ||
    driver.defaultEquipment?.name ||
    driver.equipment?.companyShare?.externalId ||
    driver.equipment?.name ||
    null
  );
}

function getTruckType(driver: Driver | DriverBasic | DriverTypeahead) {
  const { defaultEquipment } = driver.companyShare ?? {};
  if (defaultEquipment) {
    return defaultEquipment.equipmentType.name;
  } else if (driver.equipment) {
    const { equipment } = driver;
    return equipment.equipmentType.name;
  }
  return null;
}

/**
 * Get info to be shown in the following places:
 * - Orders dispatch >> Driver panel >> Driver card
 * - Orders dispatch >> Order card >> Jobs table >> "Driver & Vendor" column
 * - Assign driver or vendor menu >> Driver card
 * - Bulk Assign Multiple Jobs dialog >> Driver card
 * @param driver A driver
 * @param connectedCompany The company in the vendor job assignment if within a single job context, the driver's sender company otherwise
 * @param hideDriversVendorNameIfOwnerOperatorFeatureFlagEnabled `hide_drivers_vendor_name_if_owner_operator` feature flag value
 */
export function getDriverInfoForCards(
  driver: DriverTypeahead,
  connectedCompany: CompanyBasic | undefined,
  hideDriversVendorNameIfOwnerOperatorFeatureFlagEnabled: boolean,
) {
  const { companyShare, scheduleOn, fullName } = driver;

  const isExternal = Boolean(companyShare);
  const isAvailable = isExternal ? Boolean(companyShare?.scheduleOn) : scheduleOn;
  const equipmentName = getConsolidatedEquipmentName(driver);
  const isOwnerOperator = connectedCompany?.companyType === CompanyType.OWNER_OPERATOR;
  const driverLegalName = `${driver.firstName} ${driver.lastName}`;
  const nameMatchesCompanyName =
    connectedCompany?.legalName?.trim() === driverLegalName.trim();
  const vendorName =
    hideDriversVendorNameIfOwnerOperatorFeatureFlagEnabled &&
    isOwnerOperator &&
    nameMatchesCompanyName
      ? null
      : (connectedCompany?.legalName ?? null);
  const truckType = getTruckType(driver);

  return { isAvailable, equipmentName, fullName, truckType, vendorName };
}
